import { defineStore } from 'pinia';
import { useGameConfigStore } from './gameConfig';
import { getItem, setItem } from '../storage';

const HISTORY_KEY = 'gameHistory';
const GAME_KEY = 'gameState';

const loadGame = () => {
  let saved = null;
  try {
    saved = JSON.parse(getItem(GAME_KEY));
  } catch {
    saved = null;
  }
  const players = Array.isArray(saved?.players) ? saved.players : [];
  return {
    gameId: saved?.gameId || (players.length ? crypto.randomUUID() : null),
    startedAt: saved?.startedAt || null,
    players,
    rounds: Array.isArray(saved?.rounds) ? saved.rounds : [],
    miniGames: Array.isArray(saved?.miniGames) ? saved.miniGames : []
  };
};

const loadHistory = () => {
  try {
    const saved = JSON.parse(getItem(HISTORY_KEY));
    return Array.isArray(saved) ? saved : [];
  } catch {
    return [];
  }
};

export const useGameStore = defineStore('game', {
  state: () => ({
    ...loadGame(),
    history: loadHistory(),
    redoStack: []
  }),

  getters: {
    maxRound: (state) => state.players.length * state.miniGames.length,
    hasActiveGame: (state) =>
      state.players.length > 0 &&
      state.rounds.length < state.players.length * state.miniGames.length,

    isGameOver: (state) => {
      if (state.players.length === 0) return false;
      const totalRounds = state.players.length * state.miniGames.length;
      return state.rounds.length >= totalRounds;
    },

    currentRound: (state) => state.rounds.length + 1,
    currentPlayerIndex: (state) =>
      state.players.length ? state.rounds.length % state.players.length : 0,
    currentPlayerId: (state) => state.players[state.currentPlayerIndex]?.id,

    availableMiniGames: (state) => {
      const currentPlayerId = state.currentPlayerId;
      if (!currentPlayerId) return [];

      const playedGames = state.rounds
        .filter((round) => round.miniGame.selectedByPlayerId === currentPlayerId)
        .map((round) => round.miniGame?.id)
        .filter(Boolean);
      const gameConfig = useGameConfigStore();
      return Object.values(gameConfig.games).filter(
        (game) => state.miniGames.includes(game.id) && !playedGames.includes(game.id)
      );
    },

    playerNames: (state) => state.players.map((player) => player.name),

    canUndo: (state) => state.rounds.length > 0,
    canRedo: (state) => state.redoStack.length > 0
  },

  actions: {
    saveGameState() {
      const gameState = {
        gameId: this.gameId,
        startedAt: this.startedAt,
        players: this.players,
        rounds: this.rounds,
        miniGames: this.miniGames
      };
      setItem(GAME_KEY, JSON.stringify(gameState));
    },

    initializeGame(playerNames, gameType = 'full') {
      this.gameId = crypto.randomUUID();
      this.startedAt = new Date().toISOString();
      this.players = playerNames
        .filter((name) => name.trim())
        .map((name) => ({
          id: crypto.randomUUID(),
          name: name.trim()
        }));

      this.rounds = [];
      this.redoStack = [];

      const gameConfig = useGameConfigStore();
      this.miniGames =
        gameType === 'full' ? Object.values(gameConfig.games).map((game) => game.id) : [];

      this.saveGameState();
    },

    startRematch() {
      const names = this.players.map((player) => player.name);
      const miniGames = [...this.miniGames];
      this.initializeGame(names);
      this.setMiniGames(miniGames);
    },

    setMiniGames(selectedMiniGames) {
      this.miniGames = selectedMiniGames;
      this.saveGameState();
    },

    addRoundScores(scores, miniGameId, isDouble = false) {
      const newRound = {
        roundNumber: this.currentRound,
        miniGame: {
          id: miniGameId,
          isDouble,
          selectedByPlayerId: this.currentPlayerId
        },
        scores: scores.map(({ playerId, score }) => ({
          playerId,
          score: isDouble ? score * 2 : score
        }))
      };

      this.rounds.push(newRound);
      this.redoStack = [];
      this.saveGameState();
      this.syncHistory();
    },

    undoLastRound() {
      if (!this.canUndo) return;
      this.redoStack.push(this.rounds.pop());
      this.saveGameState();
      this.syncHistory();
    },

    redoLastRound() {
      if (!this.canRedo) return;
      this.rounds.push(this.redoStack.pop());
      this.saveGameState();
      this.syncHistory();
    },

    syncHistory() {
      if (!this.gameId) return;
      const existing = this.history.find((entry) => entry.id === this.gameId);
      const others = this.history.filter((entry) => entry.id !== this.gameId);
      if (this.rounds.length === 0) {
        this.history = others;
        this.saveHistory();
        return;
      }
      const unchanged = existing?.rounds.length === this.rounds.length;
      const now = new Date().toISOString();
      const updatedAt = unchanged && existing.updatedAt ? existing.updatedAt : now;
      this.history = [
        {
          id: this.gameId,
          startedAt: this.startedAt,
          updatedAt,
          finishedAt: this.isGameOver ? (unchanged && existing.finishedAt) || now : null,
          ...JSON.parse(
            JSON.stringify({
              players: this.players,
              rounds: this.rounds,
              miniGames: this.miniGames
            })
          )
        },
        ...others
      ].sort((a, b) =>
        (b.updatedAt ?? b.finishedAt ?? '').localeCompare(a.updatedAt ?? a.finishedAt ?? '')
      );
      this.saveHistory();
    },

    resumeFromHistory(id) {
      const entry = this.history.find((game) => game.id === id);
      if (!entry) return false;
      const copy = JSON.parse(JSON.stringify(entry));
      this.gameId = copy.id;
      this.startedAt = copy.startedAt ?? null;
      this.players = copy.players;
      this.rounds = copy.rounds;
      this.miniGames = copy.miniGames;
      this.redoStack = [];
      this.saveGameState();
      return true;
    },

    deleteHistoryEntry(id) {
      this.history = this.history.filter((entry) => entry.id !== id);
      this.saveHistory();
    },

    saveHistory() {
      try {
        setItem(HISTORY_KEY, JSON.stringify(this.history));
      } catch {
        return;
      }
    }
  }
});
